import React, { useContext, useRef, useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import BurgerButton from "../BurgerButton";
import { ModalContext, ModalType } from "@src/components/modals/ModalController";
import SchoolClosureBannerSection from "@src/components/partials/index/SchoolClosureBannerSection";
import { AppDataContext } from "../AppDataContext";
import { getRelativePath } from "@src/utils/routes";
import { logEvent } from "@src/utils/logClient";
import window from "global/window";
import NavLinks from "../partials/header/NavLinks";
import * as S from "./styles";
import FocusTrap from "focus-trap-react";
import useOnClickOutside from "@src/utils/useClickOutside";
import { useFeatureFlagsWithOverrides } from "@src/utils/useFeatureFlag";

type HeaderProps = {
  showNavBanner: boolean;
  hideNav: boolean;
  closeBannerFunction: () => void;
};

const Header: React.FC<HeaderProps> = ({ showNavBanner, closeBannerFunction, hideNav }) => {
  const data = useStaticQuery(graphql`
    query {
      logo: file(name: { eq: "classdojo_logo_176@2x" }) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
    }
  `);

  const { logo } = data;

  const [headerOpen, setHeaderOpen] = useState<boolean>(false);
  const toggleHeader = () => setHeaderOpen(!headerOpen);

  const appData = useContext(AppDataContext);
  const modalContext = useContext(ModalContext);
  const featureFlags = useFeatureFlagsWithOverrides();

  function openLoginModal() {
    logEvent({ eventValue: window.location.href, eventName: "web.external_page.navbar.log_in.tap" });
    modalContext.showModal(ModalType.Login);
  }

  function openSignupModal() {
    logEvent({
      eventValue: window.location.href,
      eventName: "web.external_page.navbar.sign_up.tap",
      metadata: featureFlags,
    });
    modalContext.showModal(ModalType.Signup);
  }
  const ref = useRef();
  useOnClickOutside(ref, () => setHeaderOpen(false));

  return (
    <S.Nav role="navigation">
      <S.HeaderContainer height="100%">
        <a href={getRelativePath("/")}>
          <S.Logo fluid={logo.childImageSharp.fluid} loading="eager" alt="ClassDojo logo" />
        </a>

        {!hideNav ? (
          <FocusTrap active={headerOpen}>
            <div role="dialog" aria-modal="true" ref={ref}>
              <BurgerButton onClick={toggleHeader} active={headerOpen} aria-expanded={headerOpen} />
              <NavLinks
                openLoginModal={openLoginModal}
                openSignupModal={openSignupModal}
                appData={appData.data}
                closeMenu={() => setHeaderOpen(false)}
                active={headerOpen}
              />
            </div>
          </FocusTrap>
        ) : null}
      </S.HeaderContainer>
      {showNavBanner ? <SchoolClosureBannerSection closeFunction={closeBannerFunction} /> : ""}
    </S.Nav>
  );
};

export default Header;
